import React, { useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, Chip, Grid, TableRow, TableCell,
  DialogContent, DialogActions, TextField, Button, Alert, Tooltip,
  TablePagination, MenuItem, Select, FormControl, InputLabel, InputAdornment,
  useTheme
} from '@mui/material';
import {
  Search, Visibility, Phone, Email, School, CalendarToday, Person
} from '@mui/icons-material';
import api from '../../utils/api';
import {
  SectionHeader, GlassPaper, GlassTable, GlassDialog, GlassDialogSection,
  GradientButton, EmptyState, ShimmerTableRows
} from './ui';
import { glass } from './ui/glassTokens';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'info' },
  { value: 'contacted', label: 'Contacted', color: 'warning' },
  { value: 'interested', label: 'Interested', color: 'primary' },
  { value: 'not_interested', label: 'Not Interested', color: 'default' },
  { value: 'enrolled', label: 'Enrolled', color: 'success' },
  { value: 'cancelled', label: 'Cancelled', color: 'error' },
];

function InstituteLeadsManagement({ apiBase = '/admin' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editFollowUp, setEditFollowUp] = useState('');
  const [editStatus, setEditStatus] = useState('');

  useEffect(() => {
    fetchLeads();
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchLeads();
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page + 1);
      params.append('limit', rowsPerPage);
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);

      const res = await api.get(`${apiBase}/institute-leads?${params.toString()}`);
      setLeads(res.data?.data?.leads || []);
      setTotal(res.data?.data?.total || 0);
    } catch (err) {
      setError('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (lead) => {
    setSelectedLead(lead);
    setEditStatus(lead.status);
    setEditNotes(lead.notes || '');
    setEditFollowUp(lead.follow_up_date ? lead.follow_up_date.split('T')[0] : '');
    setDetailOpen(true);
  };

  const handleUpdateLead = async () => {
    try {
      await api.put(`${apiBase}/institute-leads/${selectedLead.id}`, {
        status: editStatus,
        notes: editNotes,
        follow_up_date: editFollowUp || null,
      });
      setSuccess('Lead updated successfully');
      setDetailOpen(false);
      fetchLeads();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update lead');
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.put(`${apiBase}/institute-leads/${leadId}`, { status: newStatus });
      setSuccess('Status updated');
      fetchLeads();
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const statusChipSx = (statusVal) => {
    const colorMap = {
      new: { bg: 'rgba(34,211,238,0.15)', color: '#22D3EE' },
      contacted: { bg: 'rgba(245,158,11,0.15)', color: '#F59E0B' },
      interested: { bg: 'rgba(99,102,241,0.15)', color: '#6366F1' },
      not_interested: { bg: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.07)', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
      enrolled: { bg: 'rgba(16,185,129,0.15)', color: '#10B981' },
      cancelled: { bg: 'rgba(244,63,94,0.15)', color: '#F43F5E' },
    };
    const c = colorMap[statusVal] || colorMap.not_interested;
    return { fontSize: '0.7rem', fontWeight: 600, height: 22, border: 'none', background: c.bg, color: c.color };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
    '& .MuiSelect-select': {
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
    '& .MuiSvgIcon-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
    '& .MuiInputBase-input': {
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
  };

  const paginationSx = {
    color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)',
    '& .MuiSelect-select': { color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' },
    '& .MuiSvgIcon-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
    '& .MuiButtonBase-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
  };

  return (
    <Box>
      <SectionHeader
        eyebrow="Institutes"
        title="Institute Inquiries"
        description="View and manage all student inquiries for institutes"
      />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Filters */}
      <GlassPaper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by student name, email, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {STATUS_OPTIONS.map(s => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
              Total: <strong style={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)' }}>{total}</strong> inquiries
            </Typography>
          </Grid>
        </Grid>
      </GlassPaper>

      {/* Table */}
      <GlassTable
        columns={[
          { label: 'Student' },
          { label: 'Contact' },
          { label: 'Institute' },
          { label: 'Plan' },
          { label: 'Status' },
          { label: 'Message' },
          { label: 'Date' },
          { label: 'Actions', align: 'center' },
        ]}
      >
        {loading ? (
          <ShimmerTableRows count={5} cols={8} />
        ) : leads.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} sx={{ border: 0, p: 0 }}>
              <EmptyState
                icon={<School />}
                iconIndex={1}
                title="No inquiries found"
                description="Student inquiries for institutes will appear here."
              />
            </TableCell>
          </TableRow>
        ) : leads.map((lead) => (
          <TableRow key={lead.id}>
            <TableCell>
              <Typography variant="body2" fontWeight="600" sx={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)' }}>
                {lead.student?.name || 'N/A'}
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                {lead.student?.city || ''}
              </Typography>
            </TableCell>
            <TableCell>
              <Box display="flex" gap={0.5}>
                {lead.student?.email && (
                  <Tooltip title={lead.student.email}>
                    <IconButton size="small" href={`mailto:${lead.student.email}`}
                      sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }}>
                      <Email fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {lead.student?.phone && (
                  <Tooltip title={lead.student.phone}>
                    <IconButton size="small" href={`tel:${lead.student.phone}`}
                      sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }}>
                      <Phone fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{lead.institute?.name || 'N/A'}</Typography>
              <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                {lead.institute?.city || ''}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
                {lead.plan?.name || '-'}
              </Typography>
            </TableCell>
            <TableCell>
              <FormControl size="small" sx={{ minWidth: 120, ...selectSx }}>
                <Select
                  value={lead.status}
                  onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                  size="small"
                  sx={{ fontSize: '0.8rem' }}
                >
                  {STATUS_OPTIONS.map(s => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TableCell>
            <TableCell>
              <Typography variant="body2" sx={{
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)',
              }}>
                {lead.message || '-'}
              </Typography>
            </TableCell>
            <TableCell sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
              {formatDate(lead.created_at)}
            </TableCell>
            <TableCell align="center">
              <Tooltip title="View Details">
                <IconButton size="small" onClick={() => handleOpenDetail(lead)}
                  sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </GlassTable>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50]}
        sx={paginationSx}
      />

      {/* Detail Dialog */}
      <GlassDialog open={detailOpen} onClose={() => setDetailOpen(false)} title="Inquiry Details" maxWidth="sm" fullWidth>
        <DialogContent sx={{ pt: 2 }}>
          {selectedLead && (
            <Grid container spacing={2}>
              {/* Student Info */}
              <Grid item xs={12}>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                  Student Information
                </Typography>
                <Box sx={{ p: 2, mt: 1, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}` }}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                    <Person fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                    <Typography variant="body2" fontWeight="600">{selectedLead.student?.name || 'N/A'}</Typography>
                  </Box>
                  {selectedLead.student?.email && (
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Email fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                      <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
                        {selectedLead.student.email}
                      </Typography>
                    </Box>
                  )}
                  {selectedLead.student?.phone && (
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Phone fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                      <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
                        {selectedLead.student.phone}
                      </Typography>
                    </Box>
                  )}
                  {selectedLead.student?.city && (
                    <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)', mt: 0.5 }}>
                      City: {selectedLead.student.city}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Institute Info */}
              <Grid item xs={12}>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                  Institute
                </Typography>
                <Box sx={{ p: 2, mt: 1, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}` }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <School fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                    <Typography variant="body2" fontWeight="600">{selectedLead.institute?.name || 'N/A'}</Typography>
                  </Box>
                  {selectedLead.plan && (
                    <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)', mt: 0.5 }}>
                      Plan: {selectedLead.plan.name}
                      {selectedLead.plan.price_cents ? ` - ₹${(selectedLead.plan.price_cents / 100).toFixed(0)}` : ''}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Message */}
              {selectedLead.message && (
                <Grid item xs={12}>
                  <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                    Student Message
                  </Typography>
                  <Box sx={{ p: 2, mt: 1, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}` }}>
                    <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
                      {selectedLead.message}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Preferred Batch Start */}
              {selectedLead.preferred_batch_start && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarToday fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }} />
                    <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
                      Preferred Start: {formatDate(selectedLead.preferred_batch_start)}
                    </Typography>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ borderBottom: 1, borderColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.07)' }} />
              </Grid>

              {/* Editable Fields */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" sx={selectSx}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    label="Status"
                  >
                    {STATUS_OPTIONS.map(s => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Follow-up Date"
                  type="date"
                  value={editFollowUp}
                  onChange={(e) => setEditFollowUp(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add notes about this inquiry..."
                  sx={inputSx}
                />
              </Grid>

              {/* Metadata */}
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>
                  Submitted: {formatDate(selectedLead.created_at)}
                  {selectedLead.contacted_at && ` | Contacted: ${formatDate(selectedLead.contacted_at)}`}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton variant="ghost" onClick={() => setDetailOpen(false)}>Cancel</GradientButton>
          <GradientButton variant="primary" onClick={handleUpdateLead}>Update</GradientButton>
        </DialogActions>
      </GlassDialog>
    </Box>
  );
}

export default InstituteLeadsManagement;
